import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Container,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  alpha,
  Paper,
  Card,
  CardContent,
} from '@mui/material';
import {
  Security as SecurityIcon,
  PhoneAndroid as PhoneIcon,
  ContactEmergency as EmergencyIcon,
  Lock as LockIcon,
  Speed as SpeedIcon,
  CloudSync as CloudIcon,
  Shield as ShieldIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import PrimaryButton from '../components/PrimaryButton';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAuthenticated = authService.isAuthenticated();

  const features = [
    {
      icon: <PhoneIcon sx={{ fontSize: 48 }} />,
      title: '📱 Acceso Instantáneo',
      description: 'Toda tu información vital disponible al instante desde cualquier dispositivo',
    },
    {
      icon: <LockIcon sx={{ fontSize: 48 }} />,
      title: '🔐 Cifrado Extremo',
      description: 'Seguridad militar con cifrado de extremo a extremo. Solo tú puedes ver tus datos',
    },
    {
      icon: <EmergencyIcon sx={{ fontSize: 48 }} />,
      title: '🚨 Contactos de Emergencia',
      description: 'Información crítica organizada para situaciones de emergencia real',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 48 }} />,
      title: '⚡ Ultra Rápido',
      description: 'Diseño optimizado para acceso rápido cuando cada segundo cuenta',
    },
  ];

  const benefits = [
    {
      icon: '🏥',
      title: 'Información Médica',
      description: 'Tipo de sangre, alergias, medicamentos y condiciones médicas siempre a mano'
    },
    {
      icon: '👨‍👩‍👧‍👦',
      title: 'Contactos Familiares',
      description: 'Lista completa de contactos de emergencia con relaciones y números actualizados'
    },
    {
      icon: '🏠',
      title: 'Direcciones Importantes',
      description: 'Casa, trabajo, médicos y lugares importantes con detalles completos'
    },
    {
      icon: '💳',
      title: 'Información Financiera',
      description: 'Datos bancarios y financieros seguros para emergencias económicas'
    },
  ];

  const stats = [
    { number: '10K+', label: 'Usuarios Activos' },
    { number: '99.9%', label: 'Uptime' },
    { number: '24/7', label: 'Disponibilidad' },
    { number: '256-bit', label: 'Encriptación' },
  ];

  return (
    <Box className="fade-in-up">
      {/* Hero Section Moderno */}
      <Paper
        sx={{
          position: 'relative',
          minHeight: { xs: '60vh', md: '70vh' },
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
          overflow: 'hidden',
          borderRadius: { xs: 2, md: 4 },
          mb: { xs: 4, md: 6 },
          color: 'white',
        }}
      >
        {/* Elementos decorativos de fondo */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: { xs: 120, md: 200 },
            height: { xs: 120, md: 200 },
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            left: '5%',
            width: { xs: 80, md: 120 },
            height: { xs: 80, md: 120 },
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
          }}
        />

        {/* Contenido principal */}
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: 'relative', 
            zIndex: 2,
            textAlign: 'center',
            py: { xs: 6, md: 8 },
          }}
        >
          {/* Badge decorativo */}
          <Box sx={{ mb: 3 }}>
            <Chip
              label="🚨 Seguridad & Emergencias"
              sx={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                fontWeight: 500,
                px: 1,
              }}
            />
          </Box>

          {/* Título principal */}
          <Typography
            variant={isMobile ? 'h3' : 'h1'}
            component="h1"
            sx={{
              fontWeight: 800,
              color: 'white',
              mb: 3,
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' },
              fontFamily: 'Inter, sans-serif',
              lineHeight: 1.1,
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}
          >
            {isAuthenticated ? '¡Bienvenido de vuelta!' : '🚨 Tu Información Vital, Siempre Segura'}
          </Typography>

          {/* Subtítulo */}
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            component="p"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              mb: 4,
              fontWeight: 400,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              lineHeight: 1.5,
              maxWidth: '600px',
              mx: 'auto',
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
            }}
          >
            {isAuthenticated 
              ? 'Gestiona tu información de emergencia desde tu dashboard personal'
              : 'Accede a toda tu información crítica cuando más la necesitas. Segura, privada y siempre disponible.'
            }
          </Typography>

          {/* Área de acciones */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 4 }}
          >
            {isAuthenticated ? (
              <PrimaryButton
                variant="outlined"
                size="large"
                onClick={() => navigate('/dashboard')}
                sx={{ px: 6, py: 2, fontSize: '1.1rem' }}
              >
                🏠 Ir al Dashboard
              </PrimaryButton>
            ) : (
              <>
                <PrimaryButton
                  size="large"
                  onClick={() => navigate('/register')}
                  pulse
                  sx={{ px: 6, py: 2, fontSize: '1.1rem', minWidth: 200 }}
                >
                  🚀 Comenzar Gratis
                </PrimaryButton>
                <PrimaryButton
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{ px: 6, py: 2, fontSize: '1.1rem', minWidth: 200 }}
                >
                  🔐 Iniciar Sesión
                </PrimaryButton>
              </>
            )}
          </Stack>
        </Container>
      </Paper>

      {/* Estadísticas */}
      {!isAuthenticated && (
        <Container maxWidth="lg" sx={{ mb: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box textAlign="center" className="hover-lift">
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      background: 'var(--primary-gradient)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      mb: 1,
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="✨ Características Principales"
            sx={{
              mb: 3,
              background: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              fontWeight: 600,
              px: 2,
              py: 1,
            }}
          />
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontFamily: 'Inter, sans-serif',
              mb: 2,
            }}
          >
            Tecnología de <span className="gradient-text">Vanguardia</span>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto', fontWeight: 400 }}
          >
            Diseñado para situaciones críticas con la máxima seguridad y facilidad de uso
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  borderRadius: 3,
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 8px 30px rgba(102, 126, 234, 0.15)',
                  },
                }}
                className="hover-lift"
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                      fontFamily: 'Inter, sans-serif',
                      mb: 1,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.6,
                      fontSize: '0.95rem',
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box
          sx={{
            background: `linear-gradient(135deg, 
              ${alpha(theme.palette.primary.main, 0.05)} 0%, 
              ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            borderRadius: 4,
            p: { xs: 4, md: 6 },
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontFamily: 'Inter, sans-serif',
                mb: 2,
              }}
            >
              Todo lo que Necesitas en <span className="gradient-text">Un Solo Lugar</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    p: 3,
                    borderRadius: 3,
                    background: alpha(theme.palette.background.paper, 0.7),
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                    },
                  }}
                  className="smooth-transition"
                >
                  <Typography variant="h3" component="div" sx={{ fontSize: '2.5rem' }}>
                    {benefit.icon}
                  </Typography>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontFamily: 'Inter, sans-serif',
                        mb: 1,
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {benefit.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* CTA Final */}
      {!isAuthenticated && (
        <Container maxWidth="md" sx={{ mb: 8 }}>
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              background: 'var(--primary-gradient)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                fontFamily: 'Inter, sans-serif',
                mb: 2,
              }}
            >
              ¿Listo para Proteger tu Información?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
              }}
            >
              Únete a miles de usuarios que ya confían en Cuerda.cl
            </Typography>
            <PrimaryButton
              variant="outlined"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
              }}
            >
              🚀 Crear Mi Cuenta Gratis
            </PrimaryButton>
          </Box>
        </Container>
      )}
    </Box>
  );
};

export default Home; 